import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Mail } from 'lucide-react';

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-[#0F5156] text-white py-16">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors" data-testid="back-to-home">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold">Terms of Service</h1>
          </div>
          <p className="text-white/80">
            Effective Date: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
      </header>

      {/* Content */}
      <main className="py-16">
        <div className="max-w-4xl mx-auto px-6">

          {/* Intro */}
          <section className="mb-12">
            <p className="text-gray-600 leading-relaxed">
              Welcome to <strong>Link-A-Pro</strong>, Guyana's marketplace for trusted home service professionals. These Terms
              of Service ("Terms") govern your use of the Link-A-Pro website (<a href="https://link-a-pro.com" className="text-[#0F5156] hover:underline">link-a-pro.com</a>),
              our Progressive Web App available at <a href="https://app.link-a-pro.com" className="text-[#0F5156] hover:underline">app.link-a-pro.com</a>,
              and any related services (collectively, the "Service"). Link-A-Pro is operated by <strong>The TechXpert</strong> ("we", "us", "our").
            </p>
            <p className="text-gray-600 leading-relaxed mt-4">
              By accessing or using the Service, you agree to be bound by these Terms. If you do not agree, please do not use the Service.
            </p>
          </section>

          {/* 1. Definitions */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Definitions</h2>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li><strong>"Customer"</strong> – any User who uses the Service to find and engage Professionals.</li>
              <li><strong>"Professional"</strong> or <strong>"Pro"</strong> – any individual or business that lists their services on Link-A-Pro.</li>
              <li><strong>"PWA"</strong> – the Link-A-Pro Progressive Web App hosted at app.link-a-pro.com.</li>
              <li><strong>"Listing"</strong> – the profile, services, and pricing information published by a Professional.</li>
              <li><strong>"Trust Score"</strong> – Link-A-Pro's independently calculated rating reflecting verification, reviews, and performance.</li>
            </ul>
          </section>

          {/* 2. Description of Service */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Description of the Service</h2>
            <p className="text-gray-600 mb-4">
              Link-A-Pro is a digital marketplace that connects Customers with vetted local home service Professionals in Guyana,
              including but not limited to plumbing, electrical, cleaning, carpentry, masonry, painting, appliance repair, and
              landscaping services.
            </p>
            <p className="text-gray-600 mb-4">
              The Service is delivered primarily through our PWA — no app store download is required. The PWA can be accessed
              from any modern mobile or desktop browser and can be installed to your device home screen for app-like access.
            </p>
            <p className="text-gray-600">
              Link-A-Pro acts solely as a technology platform that introduces Customers and Professionals. We are <strong>not</strong> a
              party to any service agreement, transaction, or dispute between Customers and Professionals. We do not employ
              Professionals and do not perform the services listed on the platform.
            </p>
          </section>

          {/* 3. Eligibility & Accounts */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Eligibility & Accounts</h2>
            <p className="text-gray-600 mb-4">To use Link-A-Pro you must:</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-4">
              <li>Be at least 18 years of age and legally able to enter into binding contracts.</li>
              <li>Provide accurate, current, and complete information when registering.</li>
              <li>Keep your password and account credentials confidential.</li>
              <li>Notify us immediately of any unauthorized use of your account.</li>
            </ul>
            <p className="text-gray-600">
              You are responsible for all activity that occurs under your account. We may suspend or terminate accounts that
              violate these Terms, contain false information, or harm the integrity of the marketplace.
            </p>
          </section>

          {/* 4. Trust System */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. The Link-A-Pro Trust System</h2>
            <p className="text-gray-600 mb-4">
              Link-A-Pro operates a multi-layer trust system designed to help Customers identify reliable Professionals. This
              includes <strong>Trust Score</strong>, <strong>Verified Pro</strong>, <strong>Top Rated</strong>,
              <strong> Guyana Trusted</strong>, and <strong>Elite Pro</strong> badges.
            </p>
            <div className="bg-[#0F5156]/5 border-l-4 border-[#0F5156] rounded-r-xl p-5 mb-4">
              <p className="text-gray-800 font-semibold mb-2">Trust cannot be purchased.</p>
              <p className="text-gray-600">
                Professionals cannot pay Link-A-Pro to raise their Trust Score, receive a badge, or improve their ranking. All
                trust signals are calculated independently using verification checks, customer reviews, response time, completion
                rate, and dispute history. Paid subscription plans provide business tools and visibility features, but they
                <strong> never</strong> influence Trust Score, ratings, verified reviews, or any trust badge.
              </p>
            </div>
            <p className="text-gray-600">
              Link-A-Pro reserves the right to revoke any badge or rating if a Professional engages in fraud, misrepresentation,
              repeated complaints, or violations of these Terms.
            </p>
          </section>

          {/* 5. Customer Responsibilities */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Customer Responsibilities</h2>
            <p className="text-gray-600 mb-4">As a Customer, you agree to:</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Describe your service request honestly and accurately.</li>
              <li>Negotiate scope, pricing, and timelines directly with the Professional.</li>
              <li>Pay the Professional in accordance with your agreement.</li>
              <li>Provide truthful reviews based on your actual experience.</li>
              <li>Not use the platform to harass, defame, or discriminate against any user.</li>
            </ul>
          </section>

          {/* 6. Professional Responsibilities */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Professional Responsibilities</h2>
            <p className="text-gray-600 mb-4">As a Professional, you agree to:</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Provide accurate identity, licensing, and contact information.</li>
              <li>Hold all licenses, permits, and insurance required by Guyanese law for the services you offer.</li>
              <li>Honor the pricing, scope, and timelines agreed with Customers.</li>
              <li>Deliver services in a safe, professional, and lawful manner.</li>
              <li>Not solicit Customers off-platform to circumvent fees, ratings, or accountability.</li>
              <li>Comply with all reasonable requests by Link-A-Pro relating to verification.</li>
            </ul>
          </section>

          {/* 7. Subscription Plans */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Subscription Plans & Fees</h2>
            <p className="text-gray-600 mb-4">
              Professionals may subscribe to optional paid plans (such as Free, Pro, and Elite tiers) that unlock additional
              business tools — for example expanded listings, premium placement opportunities, lead boosts, branding features,
              and advanced analytics.
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-4">
              <li>Plan pricing, features, and inclusions are displayed on the Plans page and may be updated from time to time.</li>
              <li>Subscriptions renew automatically unless cancelled before the renewal date.</li>
              <li>Fees are non-refundable except where required by applicable law.</li>
              <li>Paid plans do not influence Trust Score, reviews, or trust badges.</li>
            </ul>
            <p className="text-gray-600">
              Payment processing may be handled by third-party providers. By subscribing, you agree to their terms in addition
              to ours.
            </p>
          </section>

          {/* 8. Payments & Transactions */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Payments & Transactions Between Users</h2>
            <p className="text-gray-600 mb-4">
              Unless explicitly stated otherwise within the Service, all payments for home services are arranged and made
              directly between the Customer and the Professional. Link-A-Pro does not collect, hold, or guarantee such payments.
            </p>
            <p className="text-gray-600">
              You are solely responsible for any taxes, duties, or regulatory obligations arising from your transactions on the
              platform.
            </p>
          </section>

          {/* 9. Reviews & Content */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Reviews, Ratings & User Content</h2>
            <p className="text-gray-600 mb-4">
              Reviews and ratings submitted by Customers are an essential part of the Trust System. By submitting content on
              Link-A-Pro you confirm that it is truthful, your own, and does not violate any third-party rights.
            </p>
            <p className="text-gray-600 mb-4">
              You grant Link-A-Pro a non-exclusive, worldwide, royalty-free licence to host, display, and use your content
              within the Service and in promotional materials.
            </p>
            <p className="text-gray-600">
              We may, at our discretion, remove reviews or content that are fraudulent, defamatory, off-topic, or otherwise in
              breach of these Terms.
            </p>
          </section>

          {/* 10. Prohibited Conduct */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Prohibited Conduct</h2>
            <p className="text-gray-600 mb-4">You agree not to:</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Misrepresent your identity, credentials, or licensing.</li>
              <li>Post fraudulent, misleading, or fake reviews.</li>
              <li>Engage in harassment, hate speech, or unlawful conduct.</li>
              <li>Use bots, scrapers, or automated tools to access the Service.</li>
              <li>Bypass platform fees, trust mechanisms, or verification processes.</li>
              <li>Upload malicious code or interfere with platform security.</li>
            </ul>
          </section>

          {/* 11. Disclaimers */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Disclaimers</h2>
            <p className="text-gray-600 mb-4">
              The Service is provided on an <strong>"as is"</strong> and <strong>"as available"</strong> basis. While Link-A-Pro
              performs verification checks on Professionals, we do not guarantee the quality, safety, legality, or outcome of
              any service performed by Professionals listed on the platform.
            </p>
            <p className="text-gray-600">
              We do not warrant that the Service will be uninterrupted, secure, or error-free, and we disclaim all warranties
              to the maximum extent permitted by law.
            </p>
          </section>

          {/* 12. Limitation of Liability */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Limitation of Liability</h2>
            <p className="text-gray-600 mb-4">
              To the maximum extent permitted by law, Link-A-Pro and The TechXpert shall not be liable for any indirect,
              incidental, special, consequential, or punitive damages, or for any loss of profits, revenue, data, or goodwill,
              arising from or related to your use of the Service or any services performed by Professionals.
            </p>
            <p className="text-gray-600">
              Our total aggregate liability for any claim shall not exceed the total fees you paid to Link-A-Pro in the twelve
              (12) months preceding the event giving rise to the claim, or GYD $20,000, whichever is lower.
            </p>
          </section>

          {/* 13. Indemnity */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Indemnification</h2>
            <p className="text-gray-600">
              You agree to indemnify, defend, and hold harmless Link-A-Pro, The TechXpert, and their officers, employees, and
              agents from any claim, loss, damage, or expense (including reasonable legal fees) arising from your use of the
              Service, your content, or your breach of these Terms.
            </p>
          </section>

          {/* 14. Termination */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Suspension & Termination</h2>
            <p className="text-gray-600 mb-4">
              We may suspend, restrict, or terminate your account at any time, with or without notice, if we believe you have
              breached these Terms, harmed other users, or compromised the integrity of the marketplace. You may delete your
              account at any time from within the PWA or by emailing us.
            </p>
            <p className="text-gray-600">
              Sections of these Terms that by their nature should survive termination — including but not limited to ownership,
              disclaimers, limitations of liability, and indemnity — will survive.
            </p>
          </section>

          {/* 15. Privacy */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Privacy</h2>
            <p className="text-gray-600">
              Your privacy is important to us. Our collection and use of personal information is described in our
              <Link to="/privacy" className="text-[#0F5156] hover:underline ml-1">Privacy Policy</Link>, which is incorporated
              into these Terms by reference.
            </p>
          </section>

          {/* 16. Intellectual Property */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">16. Intellectual Property</h2>
            <p className="text-gray-600">
              All content, trademarks, logos, designs, and software associated with Link-A-Pro are the property of The
              TechXpert or its licensors. You may not copy, modify, distribute, or create derivative works of any part of the
              Service without prior written consent.
            </p>
          </section>

          {/* 17. Governing Law */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">17. Governing Law & Dispute Resolution</h2>
            <p className="text-gray-600 mb-4">
              These Terms are governed by the laws of the Co-operative Republic of Guyana, without regard to its conflict of
              law provisions. Any dispute arising under these Terms shall be resolved in the competent courts of Guyana.
            </p>
            <p className="text-gray-600">
              Before initiating any formal proceedings, you agree to first attempt to resolve the dispute in good faith by
              contacting us at the email address below.
            </p>
          </section>

          {/* 18. Changes */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">18. Changes to These Terms</h2>
            <p className="text-gray-600">
              We may update these Terms from time to time. The latest version will always be posted on this page with the
              effective date. Continued use of the Service after changes are posted constitutes your acceptance of the revised
              Terms.
            </p>
          </section>

          {/* 19. Contact */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">19. Contact Us</h2>
            <p className="text-gray-600 mb-4">For questions about these Terms, please reach out:</p>
            <div className="bg-[#0F5156] text-white rounded-xl p-6">
              <a href="mailto:info@thetechxpert.com" className="flex items-center gap-2 text-lg font-medium hover:underline" data-testid="terms-contact-email">
                <Mail className="w-5 h-5" /> info@thetechxpert.com
              </a>
              <div className="mt-4 pt-4 border-t border-white/20">
                <p className="font-semibold">Link-A-Pro</p>
                <p className="text-white/80">A product of <span className="text-[#FFC312]">The TechXpert</span></p>
                <p className="text-white/80 mt-2">Georgetown, Guyana</p>
              </div>
            </div>
          </section>

        </div>
      </main>
    </div>
  );
}
